import { gql } from '@apollo/client';
import { skipToken } from '@apollo/client/react';
import { useMutation, useQuery } from 'platform/apollo';
import { useAuthAuthenticated } from 'platform/components/AuthContext';

import type {
  SubscribeToFeedMutation,
  UnSubscribeAllFeedMutation,
  UnSubscribeToFeedMutation,
  UpdateCustomerCommPreferencesMutation,
} from './__generated__/useCommsPreferences';
import {
  CommsPreferencesDocument,
  SubscribeToFeedDocument,
  UnSubscribeAllFeedDocument,
  UnSubscribeToFeedDocument,
  UpdateCustomerCommPreferencesDocument,
} from './__generated__/useCommsPreferences';

gql`
  query CommsPreferences($customerId: String!) {
    customerCommPreferences {
      getCommPreference(input: { customerUuid: $customerId }) {
        commPreference {
          marketingSmsOptedOut
          subscriptions {
            status
            feed {
              name
              description
              type
            }
            medium
            __typename
          }
        }
      }
    }
  }
`;

gql`
  mutation SubscribeToFeed(
    $userId: String!
    $feedName: String!
    $medium: [OdProtosCustomerV1Data_Medium!]
  ) {
    customerCommPreferences {
      subscribeToFeed(
        input: { customerUuid: $userId, feedName: $feedName, mediumsToSubscribe: $medium }
      ) {
        commPreference {
          subscriptions {
            status
            medium
            feed {
              name
              description
              type
            }
          }
        }
      }
    }
  }
`;

gql`
  mutation UnSubscribeToFeed(
    $userId: String!
    $feedName: String!
    $medium: [OdProtosCustomerV1Data_Medium!]
  ) {
    customerCommPreferences {
      unsubscribeFromFeed(
        input: { customerUuid: $userId, feedName: $feedName, mediumsToUnsubscribe: $medium }
      ) {
        commPreference {
          subscriptions {
            status
            medium
            feed {
              name
              description
              type
            }
          }
        }
      }
    }
  }
`;

gql`
  mutation UnSubscribeAllFeed($userId: String!) {
    customerCommPreferences {
      upsertCommPreference(input: { customerUuid: $userId, emailOptOut: true, smsOptOut: true }) {
        commPreference {
          subscriptions {
            feed {
              name
              description
            }
            medium
            status
          }
        }
      }
    }
  }
`;

gql`
  mutation UpdateCustomerCommPreferences($customerUuid: String!, $smsOptOut: Boolean) {
    customerCommPreferences {
      upsertCommPreference(input: { customerUuid: $customerUuid, smsOptOut: $smsOptOut }) {
        commPreference {
          smsOptedOut
        }
      }
    }
  }
`;

export const useCommsPreferences = () => {
  const auth = useAuthAuthenticated();
  const customerId = auth.user.customerUuid;
  return useQuery(
    CommsPreferencesDocument,
    !customerId
      ? skipToken
      : {
          fetchPolicy: 'no-cache',
          variables: {
            customerId,
          },
        },
  );
};

export const useSubscribeToFeed = (callback?: (data: SubscribeToFeedMutation) => void) => {
  const [subscribeToFeed, { loading, error }] = useMutation(SubscribeToFeedDocument, {
    onCompleted: callback,
  });

  return {
    subscribeToFeed,
    loading,
    error,
  };
};

export const useUnSubscribeToFeed = (callback?: (data: UnSubscribeToFeedMutation) => void) => {
  const [unSubscribeToFeed, { loading, error }] = useMutation(UnSubscribeToFeedDocument, {
    onCompleted: callback,
  });

  return {
    unSubscribeToFeed,
    loading,
    error,
  };
};

export const useUnSubscribeAllFeed = (callback?: (data: UnSubscribeAllFeedMutation) => void) => {
  const [unSubscribeAllFeed, { loading, error }] = useMutation(UnSubscribeAllFeedDocument, {
    onCompleted: callback,
  });

  return {
    unSubscribeAllFeed,
    loading,
    error,
  };
};

export const useUpdateCommPreferences = (
  callback?: (data: UpdateCustomerCommPreferencesMutation) => void,
) => {
  return useMutation(UpdateCustomerCommPreferencesDocument, {
    onCompleted: callback,
  });
};
